import { createAsyncThunk } from "@reduxjs/toolkit"

import {
    IPatientIdentificationData,
    IPolicyIdentificationData,
} from "@common-shared/types/policies.types"
import { IOneVerifyPolicyComment } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { CoverageHistoryHelper } from "../../helpers/coverageHistory.helper"
import { CoverageHistoryCommentsService } from "../../services/coverageHistoryComments.service"
import type {
    IThunkActionData,
    IThunkActionWithPracticeData,
} from "../../types/general.types"
import {
    policiesAddedSelectedItemComments,
    policiesDeletedSelectedItemComment,
    policiesSetSelectedItemComments,
    policiesUpdatedSelectedItemComment,
} from "../slices/policies.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import type { RootState } from "../store"

/**
 * Get single policy/patient comments
 */
export const policiesGetSinglePolicyComments = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        patient?: IPatientIdentificationData
        policy?: IPolicyIdentificationData
    }>
>("policiesGetSinglePolicyCommentsPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesGetSinglePolicyCommentsLoading"))

    // reset comments in state to avoid showing wrong comments while new ones are loading
    dispatch(policiesSetSelectedItemComments([]))

    try {
        const result = await CoverageHistoryCommentsService.getList(
            action.practice,
            action.payload.patient,
            action.payload.policy,
        )

        if (result) {
            dispatch(policiesSetSelectedItemComments(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesGetSinglePolicyCommentsLoading"))
    }
})

/**
 * Create comment for single policy/patient
 */
export const policiesCreateSinglePolicyComment = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        comment: string
        policy: IPolicyIdentificationData
        patient?: IPatientIdentificationData
        replyToCommentId?: string
    }>
>("policiesCreateSinglePolicyCommentPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesCreateSinglePolicyCommentLoading"))

    try {
        const result = await CoverageHistoryCommentsService.create(
            action.practice,
            action.payload.comment,
            action.payload.policy,
            action.payload.patient,
            action.payload.replyToCommentId,
        )

        if (result) {
            dispatch(policiesAddedSelectedItemComments(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesCreateSinglePolicyCommentLoading"))
    }
})

/**
 * Update comment for single policy/patient
 */
export const policiesUpdateSinglePolicyComment = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        commentId: string
        comment: string
    }>
>("policiesUpdateSinglePolicyCommentPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesUpdateSinglePolicyCommentLoading"))

    try {
        const result = await CoverageHistoryCommentsService.update(
            action.practice,
            action.payload.commentId,
            action.payload.comment,
        )

        if (result) {
            dispatch(policiesUpdatedSelectedItemComment(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesUpdateSinglePolicyCommentLoading"))
    }
})

/**
 * Delete comment for single policy/patient
 */
export const policiesDeleteSinglePolicyComment = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<{
        commentId: string
    }>
>("policiesDeleteSinglePolicyCommentPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("policiesDeleteSinglePolicyCommentLoading"))

    try {
        const result = await CoverageHistoryCommentsService.delete(
            action.practice,
            action.payload.commentId,
        )

        if (result) {
            dispatch(policiesDeletedSelectedItemComment(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("policiesDeleteSinglePolicyCommentLoading"))
    }
})

/**
 * New comment was received through sockets
 */
export const policiesNewCommentReceivedThroughSockets = createAsyncThunk<
    void,
    IThunkActionData<IOneVerifyPolicyComment>
>(
    "policiesNewCommentReceivedThroughSocketsPrefix",
    async (action, { dispatch, getState }) => {
        try {
            const globalState = getState() as RootState
            const { selectedItem } = globalState.policies

            if (
                !CoverageHistoryHelper.isCommentRelatedCoverageCheck(
                    selectedItem,
                    action.payload,
                )
            ) {
                return
            }

            dispatch(policiesAddedSelectedItemComments(action.payload))
        } catch (e) {}
    },
)
